<template>
  <button type="button" class="marker-row" :class="[`is-${layout}`, { 'is-selected': selected, 'is-disabled': !buttonEnabled }]"
    :disabled="!buttonEnabled" @click="locateMarkerGroup">
    <span class="marker-thumb" :class="{ 'is-loading': !thumbSettled }">
      <img v-show="imageSrc && thumbLoaded" :src="imageSrc" class="thumb-image" alt="" @load="thumbLoaded = true"
        @error="onThumbError" />
      <v-icon v-if="thumbSettled && !imageSrc" size="18" color="#4a5a60">mdi-image-outline</v-icon>
      <span v-if="!hasFullImage" class="thumb-flag" title="No full image — use Update image to add one">
        <v-icon size="12" color="#f0b968">mdi-image-off-outline</v-icon>
      </span>
    </span>

    <span class="marker-content">
      <span class="marker-label">{{ displayName }}</span>
      <span v-if="areaName" class="marker-sub">{{ areaName }}</span>
      <span v-else-if="scanName" class="marker-sub">{{ scanName }}</span>
      <span v-if="tags.length" class="marker-tags">
        <TagChip v-for="t in visibleTags" :key="t.id" :name="t.name" :color="t.color" :icon="t.icon" size="xs" />
        <span v-if="tags.length > maxTags" class="marker-tags-more">+{{ tags.length - maxTags }}</span>
      </span>
    </span>

    <span v-if="statusLabel" class="marker-status-pill" :class="statusClass">{{ statusLabel }}</span>
  </button>
</template>

<script setup>
import { defineOptions, defineProps, defineEmits, ref, computed, watch } from 'vue'
import organizationSasService from '@/services/OrganizationSasService'
import TagChip from '@/components/tags/TagChip.vue'

defineOptions({ name: "MarkerListItem" })

const props = defineProps({
  marker: Object,
  organizationId: String,
  scanName: String,
  areaName: { type: String, default: '' },
  buttonEnabled: { type: Boolean, default: true },
  layout: { type: String, default: 'list' },   // 'list' | 'grid'
  selected: { type: Boolean, default: false },
  tags: { type: Array, default: () => [] },     // resolved tag objects (id/name/color/icon) for this marker
  hasFullImage: { type: Boolean, default: true },  // false → show a "no full image" flag on the thumbnail
})

// Fewer chips in the horizontal list row (keeps it to one line); the grid card has room for more.
const maxTags = computed(() => (props.layout === 'grid' ? 3 : 2))
const visibleTags = computed(() => props.tags.slice(0, maxTags.value))

const emits = defineEmits(["onLocateMarker"])

const locateMarkerGroup = () => {
  if (!props.buttonEnabled) return
  emits("onLocateMarker", props.marker)
}

const displayName = computed(() => props.marker?.name || props.marker?.label || 'Marker')

// Status pill (anomalies carry Pending / Approved / Rejected; legacy "Accepted" maps to approved).
const statusLabel = computed(() => props.marker?.status || '')
const statusClass = computed(() => {
  const s = (props.marker?.status || '').toLowerCase()
  if (s === 'approved' || s === 'accepted') return 'is-approved'
  if (s === 'rejected') return 'is-rejected'
  if (s === 'pending') return 'is-pending'
  return 'is-neutral'
})

const imageSrc = ref(null)
const thumbLoaded = ref(false)      // <img> has decoded
const thumbSettled = ref(false)     // signing resolved (image or placeholder)

function onThumbError() {
  imageSrc.value = null
  thumbLoaded.value = false
  thumbSettled.value = true
}

watch(
  () => props.marker,
  async (newVal) => {
    thumbLoaded.value = false
    thumbSettled.value = false
    imageSrc.value = null
    if (!newVal) { thumbSettled.value = true; return }
    try {
      imageSrc.value = await organizationSasService.getSignedUrl(newVal.thumbnailPath)
    } catch {
      imageSrc.value = null
    } finally {
      thumbSettled.value = true
    }
  },
  { immediate: true }
);
</script>

<style scoped>
.marker-row {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  background: none;
  border: none;
  border-radius: 10px;
  text-align: left;
  font: inherit;
  cursor: pointer;
  color: inherit;
  transition: background 0.15s ease;
}
.marker-row.is-disabled { cursor: default; }
/* Hover: lift the row and grow the thumbnail to double size for a quick preview (row layout only). */
.marker-row.is-list:not(.is-disabled):hover { background: rgba(255, 255, 255, 0.05); }

.marker-thumb {
  position: relative;
  flex: 0 0 auto;
  width: clamp(40px, calc(28px + 0.9vw), 52px);
  height: clamp(32px, calc(22px + 0.75vw), 42px);
  border-radius: 8px;
  background: #1c272c;
  border: 1px solid rgba(255, 255, 255, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  transition: width 0.18s ease, height 0.18s ease;
}
.marker-row.is-list:not(.is-disabled):hover .marker-thumb {
  width: clamp(80px, calc(56px + 1.8vw), 104px);
  height: clamp(64px, calc(44px + 1.5vw), 84px);
}
.thumb-image { width: 100%; height: 100%; object-fit: cover; display: block; }

/* Corner flag when the marker has no full source image (re-capture adds one). */
.thumb-flag {
  position: absolute; top: 3px; right: 3px; width: 18px; height: 18px; border-radius: 6px;
  background: rgba(10, 15, 18, 0.72); border: 1px solid rgba(240, 185, 104, 0.35);
  display: flex; align-items: center; justify-content: center; z-index: 1;
}

/* Shimmer while the thumbnail is being signed / decoded. */
.marker-thumb.is-loading::after {
  content: "";
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.08), transparent);
  animation: mk-shimmer 1.2s ease-in-out infinite;
}
@keyframes mk-shimmer { 100% { transform: translateX(100%); } }
@media (prefers-reduced-motion: reduce) { .marker-thumb.is-loading::after { animation: none; } }

.marker-content {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.marker-label {
  font-size: clamp(0.82rem, calc(0.72rem + 0.12vw), 0.9rem);
  font-weight: 700;
  color: #fff;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.marker-sub {
  font-size: 0.74rem;
  color: #8aa0aa;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.marker-tags { display: flex; flex-wrap: wrap; align-items: center; gap: 4px; margin-top: 4px; }
.marker-tags-more { font-size: 0.72rem; color: #8aa0aa; font-weight: 600; }

/* Status pill */
.marker-status-pill {
  flex: 0 0 auto;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  white-space: nowrap;
  border: 1px solid transparent;
}
.marker-status-pill.is-pending { background: rgba(245, 166, 35, 0.14); color: #f0b968; border-color: rgba(245, 166, 35, 0.38); }
.marker-status-pill.is-approved { background: rgba(74, 222, 128, 0.14); color: #6ee7a0; border-color: rgba(74, 222, 128, 0.34); }
.marker-status-pill.is-rejected { background: rgba(248, 113, 113, 0.14); color: #ff9c9c; border-color: rgba(248, 113, 113, 0.4); }
.marker-status-pill.is-neutral { background: rgba(255, 255, 255, 0.06); color: #cfd8dc; border-color: rgba(255, 255, 255, 0.12); }

/* ── Grid layout (thumbnail on top, meta below) ── */
.marker-row.is-grid {
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
  padding: 8px;
}
.marker-row.is-grid .marker-thumb {
  width: 100%;
  height: auto;
  aspect-ratio: 16 / 9;
}
.marker-row.is-grid .marker-content { flex: 0 0 auto; gap: 1px; }
.marker-row.is-grid .marker-tags { margin-top: 2px; }
.marker-row.is-grid .marker-status-pill { align-self: flex-start; }
</style>
